import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, MapPin, Trophy, Users, Info } from "lucide-react";
import { MATCH_DETAILS, FULL_SQUAD } from "@/data/teamData";
import Footer from "@/components/Footer";

// Cari data pemain (foto, id) dari nama di skuad laga. Toleran terhadap variasi nama.
function findPlayer(name) {
  return (
    FULL_SQUAD.find((p) => p.name.toLowerCase() === name.toLowerCase()) ||
    FULL_SQUAD.find(
      (p) =>
        name.toLowerCase().startsWith(p.name.toLowerCase()) ||
        p.name.toLowerCase().startsWith(name.toLowerCase())
    ) ||
    null
  );
}

function SquadCard({ name, role }) {
  const p = findPlayer(name);
  const inner = (
    <>
      <div className="aspect-square relative overflow-hidden">
        {p ? (
          <img
            src={p.img}
            alt={name}
            className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-[#0A1128]" />
        )}
        <span className="absolute top-1 left-1 text-[8px] font-bold tracking-[0.1em] bg-[#E63946] text-white px-1 py-0.5 rounded leading-none">
          {role}
        </span>
      </div>
      <div className="bg-[#0A1128] border-t-2 border-[#E63946] px-1.5 py-1.5 text-center">
        <div className="font-display text-xs sm:text-sm leading-tight truncate">{name}</div>
      </div>
    </>
  );
  const cls =
    "group block bg-[#0A1128] border border-white/10 hover:border-[#E63946] overflow-hidden transition-colors";
  return p ? (
    <Link to={`/pemain/${p.id}`} className={cls}>{inner}</Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

const GOLD = "#d8b25a";

function PosterPlayer({ name, num }) {
  const p = findPlayer(name);
  const inner = (
    <>
      <div className="relative w-full">
        <span
          className="absolute left-0 bottom-6 z-20 font-display text-2xl sm:text-4xl leading-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)]"
          style={{ color: GOLD }}
        >
          {num}
        </span>
        <div className="w-full aspect-[3/4] overflow-hidden bg-[#0a1638] border-b-2" style={{ borderColor: GOLD }}>
          {p && (
            <img
              src={p.img}
              alt={name}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          )}
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0a1638 0%, transparent 55%)" }} />
        </div>
      </div>
      <span
        className="mt-1 font-display text-[10px] sm:text-xs uppercase tracking-wide text-center truncate w-full"
        style={{ color: GOLD }}
      >
        {name}
      </span>
    </>
  );
  const cls = "group relative flex flex-col items-center";
  return p ? (
    <Link to={`/pemain/${p.id}`} className={cls}>{inner}</Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

function TeamPoster({ team }) {
  // nomor urut sebagai stand-in (GK..ATT)
  let n = 0;
  const numbered = team.rows.map((r) => ({
    ...r,
    players: r.players.map((name) => ({ name, num: ++n })),
  }));

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#1b2c55] bg-gradient-to-br from-[#0c1d44] via-[#0a1530] to-[#060d22]">
      {/* aksen emas diagonal */}
      <div className="absolute -left-16 top-0 h-full w-40 -skew-x-12 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(216,178,90,0.12), transparent)" }} />
      <div className="absolute right-0 top-0 h-full w-px" style={{ background: GOLD, opacity: 0.25 }} />

      <div className="relative z-10 p-4 sm:p-5">
        {/* Header atas */}
        <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/10">
          <img src="/30sff-logo.png" alt="The 30s FF" className="w-9 h-9 object-contain" />
          <div className="font-display text-4xl leading-none tracking-tight text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
            XI
          </div>
          <div className="ml-auto text-right">
            <div className="font-display text-xl text-white leading-none">{team.name}</div>
            {team.formation && (
              <span className="inline-block mt-1.5 text-[10px] font-bold tracking-[0.2em] px-2 py-0.5 rounded text-[#060d22]" style={{ background: GOLD }}>
                {team.formation}
              </span>
            )}
          </div>
        </div>

        {/* Susunan per lini: ATT atas, GK bawah (seperti lapangan) */}
        <div className="flex flex-col gap-4 sm:gap-5">
          {[...numbered].reverse().map((r, i) => (
            <div key={i} className="flex justify-center gap-2 sm:gap-3">
              {r.players.map((pl, j) => (
                <div key={j} className="w-[22%] max-w-[96px]">
                  <PosterPlayer name={pl.name} num={pl.num} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MatchDetail() {
  const { slug } = useParams();
  const match = MATCH_DETAILS[slug];
  const [tab, setTab] = useState("lineup");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!match) {
    return (
      <div className="bg-[#050B14] text-white min-h-screen flex flex-col items-center justify-center px-5 text-center">
        <h1 className="font-display text-5xl mb-4">LAGA TIDAK DITEMUKAN</h1>
        <Link to="/" className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#D62839] text-white px-6 py-3 text-sm font-bold tracking-wider transition-colors">
          <ArrowLeft size={16} /> KEMBALI KE BERANDA
        </Link>
      </div>
    );
  }

  const hasSquad = Array.isArray(match.squad) && match.squad.length > 0;
  const hasLineup = Array.isArray(match.lineup) && match.lineup.length > 0;
  const totalPlayers = hasSquad
    ? match.squad.reduce((n, g) => n + g.players.length, 0)
    : 0;

  return (
    <div data-testid="match-detail-page" className="bg-[#050B14] text-white min-h-screen">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/30sff-logo.png" alt="The 30s FF" className="w-11 h-11 object-contain" />
            <div className="leading-none">
              <div className="font-display text-lg sm:text-xl tracking-wider">THE 30s FF</div>
              <div className="text-[10px] text-white/50 tracking-[0.2em]">OFFICIAL TEAM</div>
            </div>
          </Link>
          <Link
            to="/#pertandingan"
            data-testid="match-back"
            className="inline-flex items-center gap-2 text-xs font-bold tracking-wider text-white/80 hover:text-[#E63946] transition-colors"
          >
            <ArrowLeft size={14} /> JADWAL
          </Link>
        </div>
      </header>

      {/* Hero info laga */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#E63946]/15 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-6">
          <div className="text-xs font-bold tracking-[0.3em] text-[#E63946] mb-2 inline-flex items-center gap-2">
            <Trophy size={14} /> {match.competition}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl leading-none mb-4">
            {match.teams.map((t, i) => (
              <span key={i}>
                <span className={t === "The 30s FF" ? "text-[#E63946]" : ""}>{t}</span>
                {i < match.teams.length - 1 && <span className="text-white/30"> vs </span>}
              </span>
            ))}
          </h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
            <span className="inline-flex items-center gap-2"><Calendar size={15} className="text-[#E63946]" /> {match.dateLabel}</span>
            <span className="inline-flex items-center gap-2"><Clock size={15} className="text-[#E63946]" /> {match.time}</span>
            <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-[#E63946]" /> {match.venue}</span>
            {hasSquad && (
              <span className="inline-flex items-center gap-2"><Users size={15} className="text-[#E63946]" /> {totalPlayers} pemain dipanggil</span>
            )}
          </div>
        </div>
      </div>

      {/* Konten: tab Lineup & Pemain Dipanggil */}
      {hasLineup || hasSquad ? (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
          {/* Tab bar (jika dua-duanya ada) */}
          {hasLineup && hasSquad && (
            <div className="flex gap-1 mb-7 border-b border-white/10" data-testid="match-tabs">
              {[
                { id: "lineup", label: "LINEUP" },
                { id: "squad", label: "PEMAIN DIPANGGIL" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`px-4 py-2.5 text-xs sm:text-sm font-bold tracking-wider transition-colors border-b-2 -mb-px ${
                    tab === t.id
                      ? "border-[#E63946] text-white"
                      : "border-transparent text-white/50 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Lineup */}
          {hasLineup && (!hasSquad || tab === "lineup") && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
              {match.lineup.map((team, i) => (
                <TeamPoster key={i} team={team} />
              ))}
            </div>
          )}

          {/* Pemain dipanggil */}
          {hasSquad && (!hasLineup || tab === "squad") && (
            <div>
              <div className="mb-5 flex items-center justify-between gap-4">
                <span className="text-xs text-white/40">{totalPlayers} pemain dipanggil</span>
                <span className="text-xs text-white/40 whitespace-nowrap">Klik pemain untuk detail</span>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-3">
                {match.squad.flatMap((group) =>
                  group.players.map((name, i) => (
                    <SquadCard key={`${group.role}-${i}`} name={name} role={group.role} />
                  ))
                )}
              </div>
              {!hasLineup && (
                <div className="mt-6 inline-flex items-center gap-2 text-xs sm:text-sm bg-[#E63946]/10 border border-[#E63946]/30 text-[#E63946] px-3 py-2 rounded">
                  <Info size={15} className="flex-shrink-0" />
                  <span>Daftar di atas adalah pemain yang dipanggil. Starting lineup dirilis <b>H-1</b> sebelum laga.</span>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
          <div className="border border-dashed border-white/20 bg-[#0A1128] rounded-xl px-6 py-16 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#E63946]/15 flex items-center justify-center mb-5">
              <Users size={26} className="text-[#E63946]" />
            </div>
            <h2 className="font-display text-3xl mb-2">SKUAD & LINEUP MENYUSUL</h2>
            <p className="text-white/60 max-w-md mx-auto">
              Susunan pemain dan lineup untuk laga ini akan tampil di sini menjelang pertandingan.
              Pantau terus, ya!
            </p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
